/**
 * oRPC Integration - SSE Real-time Messaging
 *
 * STRICT RULES - THIS FILE MUST ONLY CONTAIN:
 * ✅ Integration bridge between handlers and external systems
 * ✅ oRPC client initialization and management
 * ✅ SSE connection handling and lifecycle management
 * ✅ Event streaming and callback coordination
 * ✅ Connection state management
 *
 * FORBIDDEN:
 * ❌ React imports or UI libraries
 * ❌ Direct atom mutations (must use callbacks)
 * ❌ Component imports
 * ❌ Business logic (delegate to handlers)
 * ❌ UI state management
 */

import {
  protocolHandler,
  type ProtocolHandlerCallbacks,
} from "../handlers/protocol-handler";
import type { ChatMessage } from "@pineseed/gui/types/chat";

export interface ORPCIntegrationCallbacks {
  onMessage?: (message: ChatMessage) => void;
  onConnectionStatusChange?: (connected: boolean) => void;
  onConnectionStatusUpdate?: (status: {
    connected: boolean;
    retryCount: number;
    lastError: Error | null;
    lastAttempt: number | null;
    nextRetryAt: number | null;
    isRetrying: boolean;
  }) => void;
  onError?: (error: Error) => void;
}

// Workspace-level connection tracking to prevent duplicate SSE connections
interface WorkspaceConnection {
  workspaceId: string;
  authToken: string;
  sseCleanup: (() => void) | null;
  isActive: boolean;
  callbacksRegistry: Set<ORPCIntegrationCallbacks>;
}

/**
 * oRPC Integration - Real-time messaging via SSE
 * Bridges oRPC handler with UI state using callback pattern
 *
 * Note: Party events are handled separately by party-integration.ts
 * This integration focuses solely on chat messaging via oRPC
 *
 * Idempotent SSE registration per workspace to prevent React re-render issues
 */
export class ORPCIntegration {
  private static instance: ORPCIntegration;
  private workspaceConnections = new Map<string, WorkspaceConnection>();
  private isInitializing = false;

  private constructor() {}

  public static getInstance(): ORPCIntegration {
    if (!ORPCIntegration.instance) {
      ORPCIntegration.instance = new ORPCIntegration();
    }
    return ORPCIntegration.instance;
  }

  /**
   * Initialize the oRPC integration for Chat messaging
   * Idempotent - only creates ONE SSE connection per workspace
   */
  public async initialize(
    workspaceId: string,
    callbacks: ORPCIntegrationCallbacks,
    authToken?: string,
  ): Promise<void> {
    try {
      console.log(
        "🔌 Initializing oRPC integration for workspace:",
        workspaceId,
      );

      // Prevent concurrent initialization attempts
      if (this.isInitializing) {
        console.log(
          "🔌 oRPC integration already initializing - skipping duplicate attempt",
        );
        return;
      }

      // Only initialize if we have an auth token
      if (!authToken) {
        console.warn(
          "⚠️ No auth token provided - skipping oRPC handler initialization",
        );
        return;
      }

      const connectionKey = workspaceId;
      const existingConnection = this.workspaceConnections.get(connectionKey);

      // Idempotent connection management
      if (
        existingConnection &&
        existingConnection.authToken === authToken &&
        existingConnection.isActive
      ) {
        // // Add callbacks to the registry for this workspace
        // existingConnection.callbacksRegistry.add(callbacks);

        // Notify new subscriber of current connection status
        callbacks.onConnectionStatusChange?.(true);
        return;
      }

      // If connection exists but with different token, clean it up first
      if (existingConnection && existingConnection.authToken !== authToken) {
        console.log("🔌 Auth token changed - cleaning up previous connection");
        await this.cleanupWorkspaceConnection(workspaceId);
      }

      // Set initializing flag
      this.isInitializing = true;

      try {
        // Initialize protocol handler for chat
        const { envConfig } = await import("./env");
        const baseUrl =
          envConfig.protocol.type === "rest"
            ? envConfig.servers.rest
            : envConfig.servers.orpc;

        await protocolHandler.initialize({
          baseUrl,
          token: authToken,
          callbacks: this.createChatHandlerCallbacks(workspaceId),
        });

        // Create new workspace connection
        const newConnection: WorkspaceConnection = {
          workspaceId,
          authToken,
          sseCleanup: null,
          isActive: false,
          callbacksRegistry: new Set([callbacks]),
        };

        this.workspaceConnections.set(connectionKey, newConnection);

        // Mark connection as active
        newConnection.isActive = true;
      } finally {
        this.isInitializing = false;
      }
    } catch (error) {
      console.error("❌ Failed to initialize oRPC integration:", error);
      this.isInitializing = false;

      // Don't propagate JWT token errors as they're expected when not authenticated
      if (
        error instanceof Error &&
        error.message.includes("JWT token required")
      ) {
        console.warn(
          "⚠️ JWT token not available - oRPC will be initialized when authenticated",
        );
      } else {
        // Notify all callbacks registered for this workspace
        const connection = this.workspaceConnections.get(workspaceId);
        if (connection) {
          const errorObj =
            error instanceof Error ? error : new Error(String(error));
          connection.callbacksRegistry.forEach((cb) => cb.onError?.(errorObj));
        }
      }
      // Don't throw the error to prevent infinite retries
    }
  }

  /**
   * Remove callbacks for a specific workspace (cleanup on component unmount)
   */
  public unregisterCallbacks(
    workspaceId: string,
    callbacks: ORPCIntegrationCallbacks,
  ): void {
    const connection = this.workspaceConnections.get(workspaceId);
    if (connection) {
      connection.callbacksRegistry.delete(callbacks);

      // If no more callbacks registered, cleanup the connection
      if (connection.callbacksRegistry.size === 0) {
        this.cleanupWorkspaceConnection(workspaceId);
      }
    }
  }

  /**
   * Create chat handler callbacks that broadcast to all registered callbacks for a workspace
   */
  private createChatHandlerCallbacks(
    workspaceId: string,
  ): ProtocolHandlerCallbacks {
    return {
      onMessage: (message) => {
        // console.log("📨 oRPC chat message received:", message.id);
        // Convert timestamp to Date if needed
        const chatMessage: ChatMessage = {
          ...message,
          timestamp:
            typeof message.timestamp === "string"
              ? new Date(message.timestamp)
              : message.timestamp,
        };

        // Broadcast to all registered callbacks for this workspace
        const connection = this.workspaceConnections.get(workspaceId);
        if (connection) {
          console.log(
            "📨 oRPC chat message received, broadcasting to callbacks:",
            message.id,
          );
          connection.callbacksRegistry.forEach((cb) =>
            cb.onMessage?.(chatMessage),
          );
        }
      },
      onConnectionStatusChange: (connected) => {
        console.log("🔌 oRPC chat connection status:", connected);

        // Broadcast to all registered callbacks for this workspace
        const connection = this.workspaceConnections.get(workspaceId);
        if (connection) {
          connection.callbacksRegistry.forEach((cb) =>
            cb.onConnectionStatusChange?.(connected),
          );
        }
      },
      // Note: onConnectionStatusUpdate removed as it's not part of ProtocolHandlerCallbacks interface
      onError: (error) => {
        console.error("❌ oRPC chat handler error:", error);

        // Broadcast to all registered callbacks for this workspace
        const connection = this.workspaceConnections.get(workspaceId);
        if (connection) {
          connection.callbacksRegistry.forEach((cb) => cb.onError?.(error));
        }
      },
    };
  }

  /**
   * Start SSE subscription for a specific conversation/thread
   * Called when conversation becomes active
   */
  public async startSSESubscription(
    workspaceId: string,
    conversationId: string,
    threadId: string,
  ): Promise<void> {
    const subscriptionContext = { conversationId, threadId };
    await this.startChatSSESubscription(workspaceId, subscriptionContext);
  }

  /**
   * Start SSE subscription for real-time chat message updates
   * Only one SSE connection per workspace
   */
  private async startChatSSESubscription(
    workspaceId: string,
    subscriptionContext: {
      conversationId: string;
      threadId: string;
    },
  ): Promise<void> {
    const connection = this.workspaceConnections.get(workspaceId);
    if (!connection) {
      throw new Error("No workspace connection found");
    }

    // Prevent multiple SSE subscriptions for the same workspace
    if (connection.sseCleanup) {
      console.warn(
        "🔌 Chat SSE subscription already exists for workspace - skipping duplicate",
      );
      return;
    }

    try {
      console.log(
        "🔄 Starting chat SSE subscription for workspace:",
        workspaceId,
      );

      // Ensure oRPC handler has JWT token before making calls
      if (!connection.authToken) {
        console.warn(
          "⚠️ No JWT token available for oRPC chat SSE subscription",
        );
        throw new Error("JWT token required for oRPC chat SSE subscription");
      }
      // Token is already set during initialization - no need to set it again

      // Subscribe to messages using protocol handler with required IDs per schema
      const subscription = await protocolHandler.subscribeToMessages({
        workspaceId: workspaceId,
        conversationId: subscriptionContext.conversationId,
        threadId: subscriptionContext.threadId,
      });

      let isActive = true;

      // Handle the AsyncIterable stream
      const handleStream = async () => {
        try {
          for await (const message of subscription) {
            if (!isActive) break;

            // console.log("📨 SSE message received:");
            // console.log(JSON.stringify(message, null, 2));

            // Handle different message types using type guards
            if (this.isSystemMessage(message)) {
              if (message.type === "heartbeat") {
                console.log("💓 SSE heartbeat received");
                continue;
              }

              if (message.type === "error") {
                console.error("❌ SSE error:", message.data);
                // Broadcast error to all callbacks
                connection.callbacksRegistry.forEach((cb) =>
                  cb.onError?.(new Error(JSON.stringify(message.data))),
                );
                continue;
              }
            } else if (this.isChatMessage(message)) {
              // console.log("📨 SSE chat message received");

              // Handle regular chat messages
              const chatMessage: ChatMessage = {
                id: message.id,
                content: message.content,
                role: message.role,
                timestamp:
                  typeof message.timestamp === "string"
                    ? new Date(message.timestamp)
                    : new Date(),
                threadId: message.threadId,
                conversationId: message.conversationId,
                workspaceId: message.workspaceId,
                participantId: message.participantId, // Server provides this
                isStreaming: message.isStreaming || false,
                status: message.status || "sent",
                reasoning: message.reasoning,
                references: message.references,
                metadata: message.metadata,
              };

              // Broadcast to all callbacks
              connection.callbacksRegistry.forEach((cb) =>
                cb.onMessage?.(chatMessage),
              );
            }
          }
        } catch (error) {
          if (isActive) {
            console.error("❌ Chat SSE stream error:", error);
            const errorObj =
              error instanceof Error ? error : new Error(String(error));
            // Broadcast error to all callbacks
            connection.callbacksRegistry.forEach((cb) =>
              cb.onError?.(errorObj),
            );
          }
        }
      };

      // Start handling the stream
      handleStream();

      // Set up cleanup function
      connection.sseCleanup = () => {
        console.log(
          "🔌 Stopping chat SSE subscription for workspace:",
          workspaceId,
        );
        isActive = false;
      };

      console.log(
        "✅ Chat SSE subscription started successfully for workspace:",
        workspaceId,
      );
    } catch (error) {
      console.error("❌ Failed to start chat SSE subscription:", error);
      throw error;
    }
  }

  /**
   * Cleanup a specific workspace connection
   */
  private async cleanupWorkspaceConnection(workspaceId: string): Promise<void> {
    const connection = this.workspaceConnections.get(workspaceId);
    if (connection) {
      console.log("🔌 Cleaning up workspace connection:", workspaceId);

      if (connection.sseCleanup) {
        connection.sseCleanup();
      }

      connection.isActive = false;
      connection.callbacksRegistry.clear();
      this.workspaceConnections.delete(workspaceId);
    }
  }

  /**
   * Shutdown the integration for all workspaces
   */
  public shutdown(): void {
    console.log("🔌 Shutting down oRPC integration for all workspaces");

    // Cleanup all workspace connections
    for (const [workspaceId] of this.workspaceConnections) {
      this.cleanupWorkspaceConnection(workspaceId);
    }

    console.log("✅ oRPC integration shutdown complete");
  }

  /**
   * Get connection status for a specific workspace
   */
  public isConnected(workspaceId?: string): boolean {
    if (workspaceId) {
      const connection = this.workspaceConnections.get(workspaceId);
      return (connection?.isActive && connection.sseCleanup !== null) || false;
    }

    // Check if any workspace is connected
    return Array.from(this.workspaceConnections.values()).some(
      (conn) => conn.isActive && conn.sseCleanup !== null,
    );
  }

  /**
   * Get current workspace IDs that have active connections
   */
  public getActiveWorkspaceIds(): string[] {
    return Array.from(this.workspaceConnections.entries())
      .filter(([, conn]) => conn.isActive)
      .map(([workspaceId]) => workspaceId);
  }

  /**
   * Type guard to check if message is a system message
   */
  private isSystemMessage(
    message: unknown,
  ): message is { type: string; data: unknown } {
    return typeof message === "object" && message !== null && "type" in message;
  }

  /**
   * Type guard to check if message is a chat message
   */
  private isChatMessage(
    message: unknown,
  ): message is ChatMessage & { participantId: string } {
    return (
      typeof message === "object" &&
      message !== null &&
      "id" in message &&
      "content" in message &&
      "role" in message &&
      "timestamp" in message &&
      "participantId" in message
    );
  }
}

// Export singleton instance
export const orpcIntegration = ORPCIntegration.getInstance();
